import {
  EmptyState,
  FlexBox,
  IconButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
  Skeleton,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { QueueDashboardQueueType } from 'lib-dashboard-types';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { ChangeEvent, memo, useCallback, useMemo, useState } from 'react';
import { useDebounce, useMount, useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { queuesApi } from 'apis/queues';
import { appActions } from 'app/app.actions';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import styles from './queueDashboardFilterControlQueues.module.scss';

export const QueueDashboardFilterControlQueues = memo(
  function QueueDashboardFilterControlQueues(): JSX.Element {
    const dispatch = useAppDispatch();
    const { params, replaceParams } = useAppSearchParams('/dashboard/queues');
    const flags = useFeatureFlag(['dashboard-enhanced-favorites']);
    const hasAnalyticsAccess = useHasAccess('analytics');

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const {
      asyncStatus,
      items: queues,
      changeArg,
      search,
      hasNextPage,
      nextPage,
    } = useLazyPaginatedEndpoint({
      endpoint: queuesApi.endpoints.readQueues,
      itemsSelector: ({ data }) => data.queues,
      infinite: true,
    });

    useMount(() => {
      if (!queues.length) {
        changeArg(
          {
            searchTerm,
            sort: {
              key: 'name',
            },
            isArchived: false,
          },
          { debounceTime: 0 },
        );
      }
    });

    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

    useUpdateEffect(() => {
      search(debouncedSearchTerm);

      if (debouncedSearchTerm) {
        ampli.dashboardQueuesSearchFieldSubmitted({ location: 'dropdown' });
      } else {
        ampli.dashboardQueuesSearchFieldCleared({ location: 'dropdown' });
      }
    }, [changeArg, debouncedSearchTerm]);

    const selectedQueues = useMemo(
      (): string[] =>
        params.type === 'custom' && params.queue ? [params.queue].flat() : [],
      [params],
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    };

    const handleClearSearch = () => {
      setSearchTerm('');
    };

    const handleSelectQueues = useCallback(
      ({ selectedValues }: SelectionListChangeEvent<string>) => {
        if (selectedValues.length) {
          replaceParams({
            type: 'custom',
            queue: selectedValues,
            sort_by: params.sort_by,
          });

          ampli.dashboardQueuesTypeChanged({
            type: 'custom',
            location: 'dropdown',
          });

          ampli.dashboardQueuesManuallySelected({
            count: selectedValues.length,
          });
        } else {
          const nextQueueType: QueueDashboardQueueType = hasAnalyticsAccess
            ? 'all'
            : 'agent';

          replaceParams({
            type: nextQueueType,
            sort_by: params.sort_by,
          });

          ampli.dashboardQueuesTypeChanged({
            type: nextQueueType,
            location: 'dropdown',
          });
        }
      },
      [hasAnalyticsAccess, params.sort_by, replaceParams],
    );

    const handleCreateFavorite = useCallback(async () => {
      const { favorite: createdFavorite } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'dashboard:favorite',
            arg: {
              mode: 'create',
              params: {
                queue_ids: selectedQueues,
                sort_by: params.sort_by,
              },
            },
          },
        }),
      );

      if (createdFavorite) {
        replaceParams({
          type: 'favourite',
          id: createdFavorite.id,
        });
      }
    }, [dispatch, params.sort_by, replaceParams, selectedQueues]);

    const isLoading =
      asyncStatus === 'uninitialized' || asyncStatus === 'pending';

    return (
      <>
        <FlexBox className={styles.sticky} padding="300" gap="200">
          <InputText
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search queues..."
            suffix={{
              type: 'custom',
              value: searchTerm ? (
                <FlexBox alignItems="center" padding="0 100">
                  <IconButton
                    variant="tertiary"
                    icon="close"
                    onClick={handleClearSearch}
                  />
                </FlexBox>
              ) : null,
              noWrap: true,
            }}
          />

          {flags.dashboardEnhancedFavorites && (
            <Tooltip content="New favorite list">
              <IconButton
                icon="plus"
                variant="secondary"
                size="large"
                onClick={handleCreateFavorite}
              />
            </Tooltip>
          )}
        </FlexBox>

        <SelectionList
          className={styles.list}
          isMultiple
          selectedValues={selectedQueues}
          onChange={handleSelectQueues}
          defaultActiveItemIndex={0}
        >
          {queues.map((queue) => (
            <SelectionList.Item
              key={queue.id}
              value={queue.id}
              className={styles.listItem}
            >
              {queue.name}
            </SelectionList.Item>
          ))}

          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SelectionList.Item
                key={i}
                value={i}
                className={styles.listItem}
                isDisabled
              >
                <Skeleton
                  height="16px"
                  style={{
                    width: `${50 + ((i * 37) % 50)}%`, // Randomizes skeleton widths
                  }}
                />
              </SelectionList.Item>
            ))}

          {asyncStatus === 'fulfilled' && !queues.length && (
            <FlexBox as="li" padding="300" justifyContent="center">
              <EmptyState
                title="No queues found"
                description="Try changing your search criteria."
              />
            </FlexBox>
          )}

          {asyncStatus === 'fulfilled' && hasNextPage && (
            <Waypoint onEnter={nextPage} />
          )}
        </SelectionList>
      </>
    );
  },
);
